import React, { useCallback, useEffect, useState } from "react";
import { FiX, FiLoader, FiRefreshCw, FiZap, FiActivity } from "react-icons/fi";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import "./UsageModal.css";

const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");

function tierLabel(tier, t) {
  if (tier === "light") return t.planLight || "Light";
  if (tier === "pro") return t.planPro || "Pro";
  if (tier === "business") return t.planBusiness || "Business";
  return t.planFree || "Free";
}

function nextResetLabel(resetsOn) {
  if (!resetsOn) return "—";
  if (/^\d{4}-\d{2}-\d{2}/.test(resetsOn)) {
    const [y, m, d] = resetsOn.slice(0, 10).split("-");
    return `${y}/${Number(m)}/${Number(d)}`;
  }
  return String(resetsOn);
}

/** Percent used of monthly allowance. No raw request counts in the UI. */
function usagePercent(usage) {
  const quota = Number(usage?.quota) || 0;
  const used = Number(usage?.used) || 0;
  if (quota <= 0) return null;
  return Math.min(100, Math.round((used / quota) * 100));
}

export default function UsageModal({ open, onClose }) {
  const { session, isLoggedIn, openLoginModal, openPlanModal, tier } = useAuth();
  const { texts } = useLanguage();
  const tu = texts.usage || {};
  const ta = texts.auth || {};
  const tb = texts.billing || {};
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!session?.access_token) {
      setError(tu.loginRequired || "利用状況を見るにはログインが必要です。");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/ai/quota`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || tu.loadFailed || "読み込みに失敗しました");
      setUsage(data);
    } catch (err) {
      setError(err.message || tu.loadFailed || "読み込みに失敗しました");
      setUsage(null);
    } finally {
      setLoading(false);
    }
  }, [session?.access_token, tu.loadFailed, tu.loginRequired]);

  useEffect(() => {
    if (!open) return;
    if (!isLoggedIn) {
      setUsage(null);
      setError("");
      return;
    }
    load();
  }, [open, isLoggedIn, load]);

  if (!open) return null;

  const displayTier = usage?.tier || tier || "free";
  const pct = usagePercent(usage);
  const exhausted = pct !== null && pct >= 100;
  const near = pct !== null && pct >= 80 && pct < 100;
  const period = usage?.period || "";
  const resetsOn = usage?.resetsOn || "";
  const meterClass = exhausted
    ? "usage-modal__meter-fill--out"
    : near
      ? "usage-modal__meter-fill--near"
      : "";

  return (
    <div className="usage-modal__overlay" onClick={onClose}>
      <div
        className="usage-modal__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="usage-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="usage-modal__close" onClick={onClose} aria-label="Close">
          <FiX />
        </button>
        <h2 id="usage-modal-title" className="usage-modal__title">
          <FiActivity /> {tu.title || "利用状況"}
        </h2>
        <p className="usage-modal__subtitle">
          {tu.subtitle || "今月の利用枠の消化率です。"}
        </p>

        {!isLoggedIn ? (
          <div className="usage-modal__empty">
            <p>{tu.loginRequired || "利用状況を見るにはログインが必要です。"}</p>
            <button
              type="button"
              className="usage-modal__cta"
              onClick={() => {
                onClose?.();
                openLoginModal();
              }}
            >
              {ta.login || "ログイン"}
            </button>
          </div>
        ) : loading && !usage ? (
          <p className="usage-modal__loading">
            <FiLoader className="usage-modal__spin" /> {tu.loading || "読み込み中…"}
          </p>
        ) : error && !usage ? (
          <div className="usage-modal__empty">
            <p className="usage-modal__error" role="alert">
              {error}
            </p>
            <button type="button" className="usage-modal__secondary" onClick={load}>
              <FiRefreshCw /> {tu.retry || "再試行"}
            </button>
          </div>
        ) : (
          <>
            <div className="usage-modal__plan-row">
              <span className="usage-modal__label">{tu.currentPlan || "現在のプラン"}</span>
              <span
                className={`usage-modal__plan-badge ${
                  displayTier !== "free" ? "usage-modal__plan-badge--paid" : ""
                }`}
              >
                {tierLabel(displayTier, ta)}
              </span>
            </div>

            <div className="usage-modal__percent-block" role="status">
              <div className={`usage-modal__percent ${exhausted ? "usage-modal__percent--out" : ""}`}>
                {pct === null ? "—" : `${pct}%`}
              </div>
              <div className="usage-modal__percent-label">
                {tu.percentLabel || "今月の消化率"}
              </div>
            </div>

            {pct !== null ? (
              <div className="usage-modal__meter">
                <div className="usage-modal__meter-track">
                  <div
                    className={`usage-modal__meter-fill ${meterClass}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            ) : null}

            {exhausted ? (
              <p className="usage-modal__exhausted" role="status">
                {tu.exhausted || "今月の利用枠に達しています。"}
              </p>
            ) : null}

            <dl className="usage-modal__meta">
              <div>
                <dt>{tu.resetOn || "次回リセット"}</dt>
                <dd>{nextResetLabel(resetsOn || period)}</dd>
              </div>
            </dl>

            <div className="usage-modal__actions">
              <button type="button" className="usage-modal__secondary" onClick={load} disabled={loading}>
                {loading ? (
                  <>
                    <FiLoader className="usage-modal__spin" /> {tu.loading || "読み込み中…"}
                  </>
                ) : (
                  <>
                    <FiRefreshCw /> {tu.refresh || "更新"}
                  </>
                )}
              </button>
              <button
                type="button"
                className="usage-modal__cta"
                onClick={() => {
                  onClose?.();
                  openPlanModal();
                }}
              >
                <FiZap /> {exhausted ? (tb.upgrade || "アップグレード") : (tb.plans || "プランを見る")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
